import { Badge, Button, Calendar, Empty, Flex, notification, Select, theme, Tree, Typography } from 'antd';
import dayjs from 'dayjs';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import CommonSkeletonLoader from '../../CommonSkeleton';
import { useSelector } from 'react-redux';
import CalendarDataAPI from '../../../restAPI/CalendarDataAPI';
import { Content } from 'antd/es/layout/layout';
import {
    DownOutlined,
    ArrowLeftOutlined
} from '@ant-design/icons';
import MeetingAPI from '../../../restAPI/MeetingAPI';
const { DirectoryTree } = Tree;
const { Title, Text } = Typography;


const getParentKey = (key, tree) => {
    let parentKey;
    for (let i = 0; i < tree.length; i++) {
        const node = tree[i];
        if (node.children) {
            if (node.children.some((item) => item.key === key)) {
                parentKey = node.key;
            } else if (getParentKey(key, node.children)) {
                parentKey = getParentKey(key, node.children);
            }
        }
    }
    return parentKey;
};

const ShareForwordPaper = forwardRef(({
    clickedItemType,
    openShareForwardDrawer,
    selectedDate,
    setSelectedDate,
    selectedItemToSend,
    expandedKeys,
    setExpandedKeys,
    selectedKeysToSendMeeting,
    setSelectedKeysToSendMeeting,
    isLayer1Visible,
    setIsLayer1Visible,
    shareForwardDrawerLoading,
    setShareForwardDrawerLoading,
    onCloseCopyForwardDrawer,
    isLoadingWhileSending,
    setIsLoadingWhileSending
}, ref) => {
    const { theme: { colorBgContainer } } = theme.useToken();
    const { token, companyId } = useSelector(state => state.auth)

    const [todayMeetingList, setTodayMeetingList] = useState([])
    const [searchValue, setSearchValue] = useState('');
    const [selectedMeetingFromList, setSelectedMeetingFromList] = useState(null)

    const [api, contextHolder] = notification.useNotification();
    const openNotificationWithIcon = (type, message, description, placement) => {
        api[type]({
            message: message,
            description: description,
            placement
        });
    };

    const reOrderArray = (incommingArray) => {
        return incommingArray.map((single, index) => ({
            ...single,
            order: index + 1
        }))
    }

    const handleSendButton = async () => {
        if (!selectedKeysToSendMeeting || !selectedMeetingFromList) {
            console.warn('Missing required data: selectedKeysToSendMeeting or selectedMeetingFromList');
            return;
        }

        const keyParts = selectedKeysToSendMeeting.split('-');
        let placementOfTheSend = null;
        let modifiedNewData = null;
        let attachmentsCount = 0; // Initialize attachment count

        if (keyParts?.length === 2) {
            const [categoryIndex, itemIndex] = keyParts.map(Number); // Ensure indices are numbers
            placementOfTheSend = selectedMeetingFromList?.agenda?.categories?.[categoryIndex]?.items?.[itemIndex];

            if (!placementOfTheSend) {
                console.warn('Invalid placement or no matching item found.');
                return;
            }
            attachmentsCount = 1; // For individual attachment
            modifiedNewData = {
                ...selectedItemToSend,
                eSignature: {
                    members: [],
                },
                isAddedToDB: false,
                order: (placementOfTheSend?.attachments?.length) + 1,
                shareWith: {
                    type: 'all',
                },
                voting: {
                    isEnabled: false,
                },
                _id: null,
            };
        } else if (keyParts?.length === 1) {
            const [categoryIndex] = keyParts.map(Number)
            placementOfTheSend = selectedMeetingFromList?.agenda?.categories?.[categoryIndex]
            if (!placementOfTheSend) {
                console.warn('Invalid placement or no matching category found.');
                return;
            }
            // Filter attachments to only include those with shareWith.type === 'all'
            const shareableAttachments = selectedItemToSend?.attachments?.filter(attachment => 
                attachment?.shareWith?.type === 'all'
            ) || [];
            
            // Check if there are any shareable attachments
            if (shareableAttachments.length === 0) {
                openNotificationWithIcon('warning', 'No Shareable Attachments', 'This paper item has no shareable attachments to copy. Only attachments shared with "all" can be copied.', 'bottomLeft');
                return;
            }
            
            attachmentsCount = shareableAttachments.length; // Set attachment count for paper item
            
            modifiedNewData = {
                ...selectedItemToSend,
                isAddedToDB: false,
                order: placementOfTheSend?.items.length > 0 ? placementOfTheSend?.items.length + 1 : 1,
                _id: null,
                attachments: shareableAttachments.map((attachment) => ({
                    ...attachment,
                    eSignature: { members: [] },
                    isAddedToDB: false,
                    shareWith: { type: 'all' },
                    voting: { isEnabled: false },
                    _id: null
                }))
            }
        }

        const updatedAgenda = JSON.parse(JSON.stringify(selectedMeetingFromList.agenda));
        if (keyParts?.length === 2) {
            const [categoryIndex, itemIndex] = keyParts.map(Number);
            const targetItem = updatedAgenda.categories?.[categoryIndex]?.items?.[itemIndex];

            if (targetItem) {
                if (!targetItem.attachments) {
                    targetItem.attachments = [modifiedNewData];
                } else {
                    targetItem.attachments.splice(modifiedNewData.order - 1, 0, modifiedNewData);
                }
            }
        } else if (keyParts?.length === 1) {
            const [categoryIndex] = keyParts.map(Number)
            const targetItem = updatedAgenda?.categories?.[categoryIndex];
            if (targetItem) {
                if (!targetItem.items) {
                    targetItem.items = [modifiedNewData]
                } else {
                    targetItem.items.splice(modifiedNewData.order - 1, 0, modifiedNewData);
                }
            }
        }
        updatedAgenda.categories = updatedAgenda.categories?.filter(category => Array.isArray(category.items) && category.items.length > 0);

        updatedAgenda.categories?.forEach(category => {
            category.items = reOrderArray(category.items);
            category.items.forEach(item => {
                if (Array.isArray(item.attachments) && item.attachments.length > 0) {
                    item.attachments = reOrderArray(item.attachments);
                    item.attachments.forEach(attachment => {
                        if (attachment.filePath) {
                            attachment.file = attachment.filePath;
                            delete attachment.filePath;
                        }
                        if (Array.isArray(attachment.eSignature)) {
                            const eSignatureRequestList = attachment.eSignature && attachment.eSignature.length > 0
                                ? attachment.eSignature
                                    .filter((singleRequest) => singleRequest.isRequested)
                                    .map((singleRequest) => singleRequest.userDetailsId)
                                : []
                            attachment.eSignature = {
                                members: eSignatureRequestList
                            };
                        } else {
                            attachment.eSignature = {
                                members: []
                            }
                        }
                        if (attachment.voting?.isEnabled && attachment.voting?.end?.type === "custom") {
                            attachment.voting = {
                                isEnabled: attachment?.voting?.isEnabled,
                                end: {
                                    type: attachment?.voting?.end?.type,
                                    customDate: attachment.voting.end.date // Assign end.date to customDate
                                }
                            };
                        }
                    });
                }
            });
        });

        const finalPayload = {
            originalDate: selectedMeetingFromList?.originalDate,
            agenda: updatedAgenda,
            meetingStatus: 'pending'
        }
        console.log('finalPayload :', finalPayload)

        try {
            const response = await MeetingAPI.updateAgenda(selectedMeetingFromList?._id, companyId, token, finalPayload);
            if (response.status === 200) {
                // const successMessage = attachmentsCount > 0 
                //     ? `Copy / Forward successful. ${attachmentsCount} attachment(s) copied.`
                //     : 'Copy / Forward successful.';
                // openNotificationWithIcon('success', 'Success', successMessage, 'bottomLeft');
                openNotificationWithIcon('success', 'Success', 'Copy / Forward successful.', 'bottomLeft');
                onCloseCopyForwardDrawer();
            }
        } catch (error) {
            console.log('Error in copy item :', error)
            openNotificationWithIcon('error', 'Failed', 'Copy / Forward failed.', 'bottomLeft');
        }
    };

    useImperativeHandle(ref, () => ({
        triggerSendFunction: handleSendButton,
    }))


    const agendaToTreeData = (agenda) => {
        return agenda.categories.map((category, categoryIndex) => ({
            title: category?.name,
            key: `${categoryIndex}`,
            children: clickedItemType === 'attachment' ? category.items.map((item, itemIndex) => ({
                title: item.name,
                key: `${categoryIndex}-${itemIndex}`,
            })) : [],
        }));
    }

    const defaultData = useMemo(() => {
        if (!selectedMeetingFromList || !selectedMeetingFromList.agenda) return [];
        return agendaToTreeData(selectedMeetingFromList.agenda);
    }, [selectedMeetingFromList]);

    const treeData = useMemo(() => {
        const loop = (data) =>
            data.map((item) => {
                const strTitle = item.title;
                const index = strTitle.indexOf(searchValue);
                const beforeStr = strTitle.substring(0, index);
                const afterStr = strTitle.slice(index + searchValue.length);
                const title =
                    index > -1 ? (
                        <span key={item.key}>
                            {beforeStr}
                            <span style={{ color: 'red' }}>{searchValue}</span>
                            {afterStr}
                        </span>
                    ) : (
                        <span key={item.key}>{strTitle}</span>
                    );

                if (item.children) {
                    return {
                        title,
                        key: item.key,
                        children: loop(item.children),
                    };
                }
                return {
                    title,
                    key: item.key,
                };
            });

        return loop(defaultData);
    }, [defaultData]);

    const onExpand = (newExpandedKeys) => {
        setExpandedKeys(newExpandedKeys);
    };

    const onSelect = (keys, event) => {
        const { node: { key } } = event;
        setSelectedKeysToSendMeeting(key)
    }

    const onDateSelect = (value) => {
        const formattedDate = dayjs(value).format('YYYY-MM-DD');
        setSelectedDate(formattedDate)
    }

    const disablePastDates = (current) => {
        return current < dayjs().add(-1, "day");
    };

    const getMeetingsByDate = async () => {
        try {
            setShareForwardDrawerLoading(true);
            const response = await CalendarDataAPI.getMeetingsByDateForCopyForward(selectedDate, companyId, token);
            if (response) {
                setTodayMeetingList(response?.data?.meetings);
                setShareForwardDrawerLoading(false)
            } else {
                setTodayMeetingList([]);
            }
        } catch (error) {
            setTodayMeetingList([]);
            setShareForwardDrawerLoading(false)
            console.log('Get today meetings - error :', error)
        }
    }

    useEffect(() => {
        if (openShareForwardDrawer && selectedDate) {
            getMeetingsByDate();
        }
    }, [selectedDate, openShareForwardDrawer])

    // Handle month change
    const onMonthChange = (month) => {
        const newValue = dayjs(selectedDate).month(month);
        setSelectedDate(newValue.format('YYYY-MM-DD'));
    };

    // Handle year change
    const onYearChange = (year) => {
        const newValue = dayjs(selectedDate).year(year);
        setSelectedDate(newValue.format('YYYY-MM-DD'));
    };

    // Custom header renderer for the calendar
    const headerRender = ({ value }) => {
        const months = Array.from({ length: 12 }, (_, i) => i);
        const years = Array.from({ length: 10 }, (_, i) => value.year() - 5 + i);

        return (
            <Flex justify='center' align='center' gap={8} style={{ paddingTop: 8, paddingBottom: 8 }}>
                {/* Month dropdown */}
                <Select
                    value={value.month()}
                    onChange={onMonthChange}
                    style={{ width: 120 }}
                >
                    {months.map((month) => (
                        <Select.Option key={month} value={month}>
                            {dayjs().month(month).format('MMMM')}
                        </Select.Option>
                    ))}
                </Select>

                {/* Year dropdown */}
                <Select
                    value={value.year()}
                    onChange={onYearChange}
                >
                    {years.map((year) => (
                        <Select.Option key={year} value={year}>
                            {year}
                        </Select.Option>
                    ))}
                </Select>
            </Flex>
        );
    };


    return (
        <div>
            {
                isLayer1Visible && (
                    <div>
                        <Calendar
                            fullscreen={false}
                            value={dayjs(selectedDate)}
                            onSelect={onDateSelect} // handle date selection
                            disabledDate={disablePastDates} // disable past dates
                            headerRender={headerRender}
                        />
                        <br />
                        {
                            shareForwardDrawerLoading ? <CommonSkeletonLoader itemCount={1} /> :
                                todayMeetingList && todayMeetingList.length > 0 ?
                                    (
                                        todayMeetingList.map((singleMeeting) => (
                                            <div key={singleMeeting._id} style={{
                                                marginBottom: 8,
                                                padding: '10px 24px',
                                                background: colorBgContainer,
                                                borderRadius: '8px'
                                            }}
                                                onClick={() => {
                                                    setSelectedMeetingFromList(singleMeeting)
                                                    setIsLayer1Visible(false)
                                                }}
                                            >
                                                <Flex justify='space-between' align='center' gap={16} style={{ cursor: 'pointer' }}>
                                                    <Flex justify='start' align='center' gap={16}>
                                                        <Badge color={singleMeeting?.status ? 'green' : 'gray'} />
                                                        <div>
                                                            <p>
                                                                {singleMeeting?.info?.name} <br />
                                                                <span style={{ fontSize: 12 }}>{dayjs(singleMeeting?.originalDate).format('YYYY-MM-DD')} | {dayjs(singleMeeting?.info?.startDate).format('hh:mm A')}</span>
                                                            </p>
                                                        </div>
                                                    </Flex>
                                                </Flex>
                                            </div>
                                        ))
                                    ) : (<>
                                        {/* <Content>
                                            <span>No meetings scheduled for {selectedDate}</span>
                                        </Content> */}
                                        <Flex align='center' justify='center' style={{ minHeight: 300 }} >
                                            <Empty
                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                description={
                                                    <Text>No meetings scheduled for {selectedDate}</Text>
                                                }
                                            />
                                        </Flex>
                                    </>)
                        }
                    </div>
                )
            }
            {
                !isLayer1Visible && (
                    <div>
                        <Flex justify="space-between" align="center" style={{
                            marginBottom: 8,
                            padding: '10px 24px',
                            background: colorBgContainer,
                            borderRadius: '8px',

                        }}>
                            <Flex justify="start" align="center" gap={16}>
                                <Button
                                    type="text"
                                    icon={<ArrowLeftOutlined />}
                                    onClick={() => {
                                        setSelectedMeetingFromList(null);
                                        setIsLayer1Visible(true);
                                    }}
                                />
                                <div>
                                    <p>{selectedMeetingFromList?.info?.name}<br />
                                        <span style={{ fontSize: 12 }}>{dayjs(selectedMeetingFromList?.originalDate).format("YYYY-MM-DD")} | {dayjs(selectedMeetingFromList?.info?.startDate).format('hh:mm A')}</span>
                                    </p>
                                </div>
                            </Flex>
                        </Flex>
                        {
                            (selectedMeetingFromList 
                                && (selectedMeetingFromList?.agenda === null || selectedMeetingFromList?.agenda?.categories?.length === 0)) ? (
                                    <>
                                        <Flex align='center' justify='center' style={{ minHeight: 10 }} >
                                            <Empty
                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                description={
                                                    <Text>No paper categories available.</Text>
                                                }
                                            />
                                        </Flex>
                                    </>
                            ):( 
                                <DirectoryTree
                                    showLine
                                    onExpand={onExpand}
                                    switcherIcon={<DownOutlined />}
                                    treeData={treeData}
                                    style={{ padding: 24 }}
                                    onSelect={onSelect}
                                    expandedKeys={expandedKeys}
                                />
                            )
                        }
                    </div>
                )
            }
            {contextHolder}
        </div>
    )
})

export default ShareForwordPaper